import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { recordsApi, jobsApi, CERT_TYPES } from "../../../service/certificate"
import { getAllSessions } from "../../../service/sessions"
import { getAllTerms } from "../../../service/term-management"
import { selectBranches } from "../../../core/data/redux/slice/branchesSlice"

const typeLabel = (v: string) => CERT_TYPES.find(t => t.value === v)?.label || v
const asArray = (r: any): any[] =>
  Array.isArray(r) ? r : Array.isArray(r?.sessions) ? r.sessions : Array.isArray(r?.data) ? r.data : Array.isArray(r?.terms) ? r.terms : []

// Prefer the frozen snapshot on the record; fall back to the populated ref.
const campusOf = (r: any) => r.campusCode || r.branch?.campusNo || r.branch?.name || ""
const sessionOf = (r: any) => r.sessionName || r.session?.title || r.session?.name || ""
const termOf = (r: any) => r.termName || r.term?.title || r.term?.name || ""
const classOf = (r: any) => r.className || r.class?.name || ""
const templateOf = (r: any) => r.templateName || r.template?.name || ""
const genByOf = (r: any) =>
  r.generatedByName || [r.generatedBy?.firstName, r.generatedBy?.lastName].filter(Boolean).join(" ").trim() || ""

// Type-specific one-line summary for a roster entry (mirrors RMS).
const rosterDetail = (kind: string, s: any) => {
  if (!s) return "—"
  if (kind === "class_result") {
    const p: string[] = []
    if (s.position_number) p.push(`#${s.position_number}`)
    if (s.grade_letter) p.push(`Grade ${s.grade_letter}`)
    if (s.percent != null) p.push(`${Number(s.percent).toFixed(1)}%`)
    return p.join(" · ") || "—"
  }
  if (kind === "sports") {
    const p: string[] = []
    if (s.sport_name) p.push(s.sport_name)
    if (s.position_label) p.push(s.position_label)
    else if (s.position_number) p.push(`#${s.position_number}`)
    return p.join(" · ") || "—"
  }
  if (kind === "participation") {
    const names = Array.isArray(s.competition_names) ? s.competition_names : []
    const pos = s.position_label || (s.position_number ? `#${s.position_number}` : "")
    return [names.join(", "), pos].filter(Boolean).join(" · ") || "—"
  }
  if (kind === "appreciation") return s.attribution_name || "—"
  return "—"
}

const parseRoster = (r: any): any[] => {
  const raw = r?.studentsData
  if (Array.isArray(raw)) return raw
  if (typeof raw === "string") { try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [] } catch { return [] } }
  return []
}

const CertRecords = () => {
  const branches = useSelector(selectBranches)

  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [sessionFilter, setSessionFilter] = useState("")
  const [termFilter, setTermFilter] = useState("")
  const [campusFilter, setCampusFilter] = useState("")
  const [sessions, setSessions] = useState<any[]>([])
  const [terms, setTerms] = useState<any[]>([])

  const [rosterRow, setRosterRow] = useState<any | null>(null)
  const [deleteRow, setDeleteRow] = useState<any | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [zipping, setZipping] = useState(false)
  const [zipProgress, setZipProgress] = useState("")

  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [pagination, setPagination] = useState<any>(null)

  const fetchRecords = async (pageNum = 1) => {
    setLoading(true)
    try {
      const params: Record<string, any> = { page: pageNum, limit: pageSize }
      if (search.trim()) params.q = search.trim()
      if (typeFilter) params.type = typeFilter
      if (sessionFilter) params.session = sessionFilter
      if (termFilter) params.term = termFilter
      if (campusFilter) params.campus = campusFilter
      const res = await recordsApi.list(params)
      if (res.success) { setItems(res.data); setPagination(res.pagination); setPage(pageNum) }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load records")
    } finally { setLoading(false) }
  }

  useEffect(() => {
    getAllSessions({ all: true, limit: 1000 } as any).then((r: any) => setSessions(asArray(r))).catch(() => setSessions([]))
    getAllTerms({ limit: 1000 } as any).then((r: any) => setTerms(asArray(r))).catch(() => setTerms([]))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => fetchRecords(1), search ? 350 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, sessionFilter, termFilter, campusFilter])

  const openPdf = async (id: string, dl = false) => {
    try {
      const res = await recordsApi.pdfUrl(id)
      if (res.success && res.url) {
        if (dl) { const a = document.createElement("a"); a.href = res.url; a.download = ""; a.target = "_blank"; document.body.appendChild(a); a.click(); a.remove() }
        else window.open(res.url, "_blank")
      } else toast.error("Could not get the PDF link")
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to open PDF") }
  }

  const confirmDelete = async () => {
    if (!deleteRow) return
    setDeleting(true)
    try { await recordsApi.remove(deleteRow._id); toast.success("Record + PDF deleted"); setDeleteRow(null); fetchRecords(page) }
    catch (err: any) { toast.error(err?.response?.data?.message || "Delete failed") }
    finally { setDeleting(false) }
  }

  const downloadZip = async () => {
    setZipping(true); setZipProgress("Starting…")
    try {
      const params: Record<string, any> = {}
      if (typeFilter) params.type = typeFilter
      if (sessionFilter) params.session = sessionFilter
      if (campusFilter) params.campus = campusFilter
      const start = await recordsApi.zip(params)
      const poll = async () => {
        try {
          const j = await jobsApi.get(start.jobId)
          const job = j.data
          if (job.status === "completed") { if (job.result?.url) window.open(job.result.url, "_blank"); else toast.error("No ZIP URL returned"); setZipping(false); setZipProgress("") }
          else if (job.status === "failed") { toast.error(job.error || "ZIP failed"); setZipping(false); setZipProgress("") }
          else { setZipProgress("Building ZIP…"); setTimeout(poll, 2000) }
        } catch { setTimeout(poll, 2500) }
      }
      poll()
    } catch (err: any) { toast.error(err?.response?.data?.message || "Failed to start ZIP"); setZipping(false); setZipProgress("") }
  }

  const anyFilter = search || typeFilter || sessionFilter || termFilter || campusFilter
  const clearFilters = () => { setSearch(""); setTypeFilter(""); setSessionFilter(""); setTermFilter(""); setCampusFilter("") }

  const rosterStudents = useMemo(() => parseRoster(rosterRow), [rosterRow])

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">Records</h3>
            <p className="text-muted mb-0">
              All generated certificates. Each row is one rendered PDF.
              {pagination && <span className="ms-2 fw-bold">({pagination.total})</span>}
            </p>
          </div>
          <button className="btn btn-outline-success" onClick={downloadZip} disabled={zipping}>
            {zipping ? <><span className="spinner-border spinner-border-sm me-1" />{zipProgress}</> : <><i className="ti ti-file-zip me-1" />Download ZIP</>}
          </button>
        </div>

        {/* Filters */}
        <div className="row g-2 mb-2">
          <div className="col-md"><input className="form-control" placeholder="Search name / GR / template…" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div className="col-md">
            <select className="form-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="col-md">
            <select className="form-select" value={sessionFilter} onChange={e => setSessionFilter(e.target.value)}>
              <option value="">All sessions</option>
              {sessions.map(s => <option key={s._id} value={s._id}>{s.branch?.name ? `${s.branch.name} — ` : ""}{s.title || s.name}</option>)}
            </select>
          </div>
          <div className="col-md">
            <select className="form-select" value={termFilter} onChange={e => setTermFilter(e.target.value)}>
              <option value="">All terms</option>
              {terms.map(t => <option key={t._id} value={t._id}>{t.title || t.name}</option>)}
            </select>
          </div>
          <div className="col-md">
            <select className="form-select" value={campusFilter} onChange={e => setCampusFilter(e.target.value)}>
              <option value="">All campuses</option>
              {branches.map((b: any) => <option key={b._id} value={b._id}>{b.code ? `${b.code} — ` : ""}{b.name}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-2 small text-muted">
          {loading ? "Loading…" : `${pagination?.total ?? items.length} record(s)`}
          {anyFilter && <button className="btn btn-link btn-sm p-0 ms-2" onClick={clearFilters}>Clear filters</button>}
        </div>

        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Student</th><th>Type</th><th>Template</th><th>Context</th>
                  <th>Details</th><th>Size</th><th>Generated</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">No certificates match these filters.</td></tr>
                ) : items.map(r => {
                  const ctx = [sessionOf(r), termOf(r), classOf(r), campusOf(r)].filter(Boolean).join(" · ")
                  return (
                    <tr key={r._id}>
                      <td>
                        {r.student ? (
                          <>
                            <div className="fw-semibold">{r.studentName || "—"}</div>
                            <div className="text-muted small">{r.grNumber || ""}</div>
                          </>
                        ) : (
                          <>
                            <div className="fw-semibold">Batch <span className="badge bg-success-transparent ms-1">{r.studentCount || 0} students</span></div>
                            <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => setRosterRow(r)}>View students</button>
                          </>
                        )}
                      </td>
                      <td><span className="badge bg-primary-transparent">{typeLabel(r.certificateType)}</span></td>
                      <td>{templateOf(r) || "—"}</td>
                      <td className="small text-muted">{ctx || "—"}</td>
                      <td className="small text-muted">Combined PDF · {r.studentCount || 0} pages</td>
                      <td>{r.pageFormat ? <span className={`badge ${String(r.pageFormat).startsWith("A4") ? "bg-success-transparent" : "bg-secondary-transparent"}`}>{r.pageFormat}</span> : <span className="text-muted">—</span>}</td>
                      <td className="small">
                        <div>{r.generatedAt ? new Date(r.generatedAt).toLocaleString() : "—"}</div>
                        {genByOf(r) && <div className="text-muted">by {genByOf(r)}</div>}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-link btn-sm p-0 me-2" onClick={() => openPdf(r._id, false)}>Open</button>
                        <button className="btn btn-link btn-sm p-0 me-2" onClick={() => openPdf(r._id, true)}>Download</button>
                        <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setDeleteRow(r)}>Delete</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => fetchRecords(page - 1)}>Prev</button>
              <span className="small">Page {page} / {pagination.totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= pagination.totalPages} onClick={() => fetchRecords(page + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>

      {/* Batch roster modal */}
      {rosterRow && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={() => setRosterRow(null)}>
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Batch ({rosterStudents.length} student{rosterStudents.length === 1 ? "" : "s"})</h5>
                <button className="btn-close" onClick={() => setRosterRow(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3 small">
                  <div className="col"><div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Type</div>{typeLabel(rosterRow.certificateType)}</div>
                  <div className="col"><div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Template</div>{templateOf(rosterRow) || "—"}</div>
                  <div className="col"><div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Context</div>{[sessionOf(rosterRow), campusOf(rosterRow)].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="col"><div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Generated by</div>{genByOf(rosterRow) || "—"}</div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead className="table-light">
                      <tr><th>Page</th><th>GR</th><th>Name</th><th>Class</th><th>Section</th><th>Father / Mother</th><th>Detail</th></tr>
                    </thead>
                    <tbody>
                      {rosterStudents.map((s, i) => (
                        <tr key={s.student_id || `${s.gr_number}-${i}`}>
                          <td>{s.page || i + 1}</td>
                          <td className="font-monospace small">{s.gr_number || "—"}</td>
                          <td className="fw-semibold">{s.full_name || "—"}</td>
                          <td>{s.class_name || "—"}</td>
                          <td>{s.section_name || "—"}</td>
                          <td>{s.fathers_name || s.mothers_name || "—"}</td>
                          <td className="text-muted">{rosterDetail(rosterRow.certificateType, s)}</td>
                        </tr>
                      ))}
                      {!rosterStudents.length && <tr><td colSpan={7} className="text-center text-muted py-3">No per-student snapshot for this row.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={() => setRosterRow(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteRow && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
                <h4 className="mt-2">Delete Record</h4>
                <p className="text-muted">Permanently delete this {deleteRow.student ? "certificate" : `batch (${deleteRow.studentCount || "multi-student"} students)`}? The PDF is removed too. Irreversible.</p>
                <div className="d-flex justify-content-center gap-2">
                  <button className="btn btn-light" onClick={() => setDeleteRow(null)} disabled={deleting}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>{deleting ? "Deleting…" : "Yes, Delete"}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CertRecords
